import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { fetchCapacityCalendar } from '@/services/capacityCalendarService';
import { confirmCapacityImport, deleteImportRecord, fetchImportRecord, fetchImportRecords, parseCapacityPlan } from '@/services/capacityImportService';
import logger from '@/services/logger';
import { fetchVendors } from '@/services/vendorService';
import { useAccessStore } from '@/store/accessStore';
import type { CapacityAllocationMap, WeekCapacityAllocation } from '@/types/capacityCalendar';
import type {
  CapacityImportConfirmResult,
  CapacityImportRecordSummary,
  CapacityMetricKey,
  CapacityPlanParseResult,
  CapacityPlanProductionLine,
  CapacityPlanWeek,
  CapacitySettingInput,
} from '@/types/capacityImport';
import type { CapacityType } from '@/types/capacityType';
import type { VendorSummary } from '@/types/vendor';
import { extractErrorMessage } from '@/utils/apiError';
import { isoWeekNumber } from '@/utils/isoWeek';

interface OverwriteTarget {
  vendorId: string;
  terminationTypeId: string;
  weekStart: string;
}

// Previews stored before canonical metric keys existed carry only the sheet label;
// infer the key with the same mapping the parser prompt uses so old records stay confirmable.
function inferMetricKey(name: string): CapacityMetricKey {
  const normalized = name.trim().toLowerCase();
  if (normalized.startsWith('remained')) return 'remainedCapacity';
  if (normalized.startsWith('capacity')) return 'capacity';
  if (normalized.startsWith('input')) return 'input';
  if (normalized.includes('line') || normalized.includes("q'ty") || normalized.includes('qty')) return 'lineQty';
  return 'other';
}

// Stored previews snapshot vendor UUIDs at parse time; a vendor deleted and re-imported
// since then carries a new id. Rebind stale ids by name/alias (the backend's own matching
// keys) so old records stay confirmable; ids with no current match are cleared so the UI
// asks the user to re-pick instead of the confirm failing on a dangling id.
function rebindVendors(preview: CapacityPlanParseResult, vendors: VendorSummary[]): CapacityPlanParseResult {
  const currentIds = new Set(vendors.map((vendor) => vendor.id));
  const byKey = new Map<string, VendorSummary>();
  vendors.forEach((vendor) => {
    byKey.set(vendor.name.trim().toLowerCase(), vendor);
    if (vendor.alias) byKey.set(vendor.alias.trim().toLowerCase(), vendor);
  });
  const rebind = (stale: VendorSummary): VendorSummary | null =>
    byKey.get(stale.name.trim().toLowerCase()) ?? (stale.alias ? (byKey.get(stale.alias.trim().toLowerCase()) ?? null) : null);

  const match = preview.vendorMatch && !currentIds.has(preview.vendorMatch.id) ? rebind(preview.vendorMatch) : preview.vendorMatch;
  return {
    ...preview,
    vendorMatch: match,
    vendorMatchConfidence: match ? preview.vendorMatchConfidence : 'none',
    productionLines: preview.productionLines.map((line) => {
      if (!line.vendorId || currentIds.has(line.vendorId)) return line;
      const rebound = line.vendorCode ? byKey.get(line.vendorCode.trim().toLowerCase()) : null;
      return { ...line, vendorId: rebound?.id ?? null };
    }),
  };
}

const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;

// Matches the backend's weekStart normalization so overlap detection lines up 1:1.
function mondayOf(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  const offset = (date.getUTCDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
  return new Date(date.getTime() - offset * MS_PER_DAY).toISOString().slice(0, 'YYYY-MM-DD'.length);
}

function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  return new Date(date.getTime() + days * MS_PER_DAY).toISOString().slice(0, 'YYYY-MM-DD'.length);
}

function withYear(isoDate: string, year: number): string {
  return `${String(year).padStart('YYYY'.length, '0')}${isoDate.slice('YYYY'.length)}`;
}

function snappedWeek(startDate: string): CapacityPlanWeek {
  const weekStart = mondayOf(startDate);
  return { weekNumber: isoWeekNumber(weekStart), startDate: weekStart, endDate: addDays(weekStart, DAYS_PER_WEEK - 1) };
}

export type BucketDiffStatus = 'new' | 'increased' | 'decreased' | 'changed' | 'unchanged' | 'skipped';

export interface BucketDiff {
  vendorId: string;
  terminationTypeId: string;
  weekStart: string;
  status: BucketDiffStatus;
  before: WeekCapacityAllocation | null;
  proposedCapacity: number;
  proposedPlanning: number;
  planningDelta: number | null;
  planningDeltaPercent: number | null;
  existingPoQuantity: number;
  overbookedBy: number;
  willWrite: boolean;
}

const PERCENT = 100;

export const useCapacityImportStore = defineStore('capacityImport', () => {
  const result = ref<CapacityPlanParseResult | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref<string | null>(null);
  const confirmResult = ref<CapacityImportConfirmResult | null>(null);
  const records = ref<CapacityImportRecordSummary[]>([]);
  const recordsLoading = ref(false);
  // Per-week overwrite opt-ins keyed by `${lineIndex}:${mondayWeekStart}`.
  const overwriteWeeks = ref<Record<string, boolean>>({});
  const existingAllocations = ref<CapacityAllocationMap>({});
  // Buckets written by confirms in this session, keyed by `${vendorId}:${terminationTypeId}:${weekStart}`.
  // Re-saving after an edit must be able to update them without a fresh overwrite tick.
  const sessionSavedWeeks = ref<Map<string, OverwriteTarget>>(new Map());

  // Mirrors the server's vendor resolution order: the line's own vendor wins; a line with
  // an unresolved vendorCode gets no fallback; otherwise the exact page-level match applies.
  function effectiveVendorId(line: CapacityPlanProductionLine): string | null {
    if (line.vendorId) return line.vendorId;
    if (line.vendorCode) return null;
    return result.value?.vendorMatchConfidence === 'exact' ? (result.value.vendorMatch?.id ?? null) : null;
  }

  // Identical (code, factor) duplicates are collapsed; conflicting duplicates survive so
  // conflictingFactorCodes can detect them and block saving.
  // Boundary: line.terminationTypeCode is the frozen parse-result field (JSONB-persisted);
  // it's mapped explicitly here onto the settings-upsert payload's capacityTypeCode.
  const extractedSettings = computed<CapacitySettingInput[]>(() => {
    if (!result.value) return [];
    const seen = new Set<string>();
    return result.value.productionLines
      .flatMap((line) =>
        !line.skipped && line.terminationTypeId !== null && line.capacityFactor !== null
          ? [{ capacityTypeCode: line.terminationTypeCode, capacityFactor: line.capacityFactor }]
          : []
      )
      .filter((setting) => {
        const key = `${setting.capacityTypeCode}:${setting.capacityFactor}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  });

  const conflictingFactorCodes = computed<string[]>(() => {
    const codes = extractedSettings.value.map((setting) => setting.capacityTypeCode);
    return [...new Set(codes.filter((code) => codes.indexOf(code) !== codes.lastIndexOf(code)))];
  });

  const saveableLines = computed(
    () => result.value?.productionLines.filter((line) => !line.skipped && line.terminationTypeId !== null) ?? []
  );

  // Weeks in this plan that already have an allocation, per line — those are kept
  // untouched on save unless their per-week overwrite checkbox is ticked.
  const existingWeekCounts = computed<number[]>(() => {
    if (!result.value) return [];
    const weekKeys = result.value.weeks.map((week) => mondayOf(week.startDate));
    return result.value.productionLines.map((line) => {
      const vendorId = effectiveVendorId(line);
      if (!vendorId || !line.terminationTypeId) return 0;
      const byWeek = existingAllocations.value[`${vendorId}:${line.terminationTypeId}`];
      if (!byWeek) return 0;
      return weekKeys.filter((weekKey) => byWeek[weekKey] !== undefined).length;
    });
  });

  const totalExistingOverlap = computed(() => existingWeekCounts.value.reduce((sum, count) => sum + count, 0));

  function existingAllocationFor(lineIndex: number, weekStart: string) {
    const line = result.value?.productionLines[lineIndex];
    if (!line || !line.terminationTypeId) return null;
    const vendorId = effectiveVendorId(line);
    if (!vendorId) return null;
    return existingAllocations.value[`${vendorId}:${line.terminationTypeId}`]?.[mondayOf(weekStart)] ?? null;
  }

  function weekAlreadyPlanned(lineIndex: number, weekStart: string): boolean {
    return existingAllocationFor(lineIndex, weekStart) !== null;
  }

  function plannedPoCount(lineIndex: number, weekStart: string): number {
    return existingAllocationFor(lineIndex, weekStart)?.purchaseOrders.length ?? 0;
  }

  // Planned POs inside overlapping weeks — overwriting replaces capacity underneath them.
  // Several lines can resolve to the same vendor:terminationType bucket; count each bucket once.
  const plannedPoTotal = computed(() => {
    const parsed = result.value;
    if (!parsed) return 0;
    const countedBuckets = new Set<string>();
    return parsed.productionLines.reduce((sum, line, lineIndex) => {
      const vendorId = effectiveVendorId(line);
      if (!vendorId || !line.terminationTypeId) return sum;
      return (
        sum +
        parsed.weeks.reduce((weekSum, week) => {
          const bucket = `${vendorId}:${line.terminationTypeId}:${mondayOf(week.startDate)}`;
          if (countedBuckets.has(bucket)) return weekSum;
          countedBuckets.add(bucket);
          return weekSum + plannedPoCount(lineIndex, week.startDate);
        }, 0)
      );
    }, 0);
  });

  function isOverwriteSelected(lineIndex: number, weekStart: string): boolean {
    return overwriteWeeks.value[`${lineIndex}:${mondayOf(weekStart)}`] === true;
  }

  function toggleOverwriteWeek(lineIndex: number, weekStart: string): void {
    const key = `${lineIndex}:${mondayOf(weekStart)}`;
    overwriteWeeks.value[key] = !overwriteWeeks.value[key];
    confirmResult.value = null;
  }

  const hasExistingData = computed(() => Object.keys(existingAllocations.value).length > 0);
  const overbookAcknowledged = ref(false);

  // Mirrors the backend's buildDrafts: same-bucket lines sum; a null capacity cell contributes
  // nothing; a bucket with no capacity at all is not proposed, so it never appears in the diff.
  const bucketDiffs = computed<Map<string, BucketDiff>>(() => {
    const parsed = result.value;
    const diffs = new Map<string, BucketDiff>();
    if (!parsed) return diffs;

    interface ProposedBucket {
      vendorId: string;
      terminationTypeId: string;
      weekStart: string;
      capacity: number;
      planning: number;
      factor: number | null;
    }
    const proposed = new Map<string, ProposedBucket>();
    // A tick is consent for the whole bucket, not just the ticking line's own cell — confirmImport
    // registers it as an overwrite target before checking that line's capacity, so a tick on a
    // null-cell line still counts here. Collected before the null-capacity check below so it
    // isn't skipped for the very lines it's most needed for.
    const tickedBuckets = new Set<string>();

    parsed.productionLines.forEach((line, lineIndex) => {
      if (line.skipped) return;
      const vendorId = effectiveVendorId(line);
      const { terminationTypeId } = line;
      if (!vendorId || !terminationTypeId) return;
      const capacityValues = line.metrics.find((metric) => metric.key === 'capacity')?.values ?? [];
      parsed.weeks.forEach((week, weekIndex) => {
        const weekStart = mondayOf(week.startDate);
        const key = `${vendorId}:${terminationTypeId}:${weekStart}`;
        if (overwriteWeeks.value[`${lineIndex}:${weekStart}`] === true) tickedBuckets.add(key);
        const capacity = capacityValues[weekIndex] ?? null;
        if (capacity === null) return;
        const factor = line.capacityFactor ?? null;
        const planning = Math.round(capacity * (factor ?? 1));
        const current = proposed.get(key);
        if (current) {
          current.capacity += capacity;
          current.planning += planning;
          // Mirrors the backend's buildDrafts: contributing lines with differing factors
          // make the bucket's factor ambiguous, so it's nulled rather than picking one.
          if (current.factor !== factor) current.factor = null;
          return;
        }
        proposed.set(key, { vendorId, terminationTypeId, weekStart, capacity, planning, factor });
      });
    });

    proposed.forEach((bucket, key) => {
      const before = existingAllocations.value[`${bucket.vendorId}:${bucket.terminationTypeId}`]?.[bucket.weekStart] ?? null;
      const willWrite = before === null || tickedBuckets.has(key) || sessionSavedWeeks.value.has(key);
      const existingPoQuantity = (before?.purchaseOrders ?? []).reduce((sum, po) => sum + po.quantity, 0);
      const beforePlanning = before?.planningCapacity ?? null;
      const planningDelta = beforePlanning === null ? null : bucket.planning - beforePlanning;
      diffs.set(key, {
        vendorId: bucket.vendorId,
        terminationTypeId: bucket.terminationTypeId,
        weekStart: bucket.weekStart,
        status: diffStatus(before, bucket.planning, bucket.capacity, bucket.factor, willWrite),
        before,
        proposedCapacity: bucket.capacity,
        proposedPlanning: bucket.planning,
        planningDelta,
        planningDeltaPercent:
          planningDelta === null || !beforePlanning ? null : Math.round((planningDelta / beforePlanning) * PERCENT * 10) / 10,
        existingPoQuantity,
        overbookedBy: willWrite ? Math.max(0, existingPoQuantity - bucket.planning) : 0,
        willWrite,
      });
    });

    return diffs;
  });

  function diffStatus(
    before: WeekCapacityAllocation | null,
    proposedPlanning: number,
    proposedCapacity: number,
    proposedFactor: number | null,
    willWrite: boolean,
  ): BucketDiffStatus {
    if (before === null) return 'new';
    if (!willWrite) return 'skipped';
    const beforePlanning = before.planningCapacity ?? 0;
    if (proposedPlanning > beforePlanning) return 'increased';
    if (proposedPlanning < beforePlanning) return 'decreased';
    // Planning can round to the same value while capacity or factor still differs
    // (e.g. capacity 100 @ factor 1.0 vs 1.004) — that's a real edit, not a no-op.
    const capacityChanged = proposedCapacity !== (before.capacity ?? null);
    const factorChanged = proposedFactor !== (before.capacityFactor ?? null);
    return capacityChanged || factorChanged ? 'changed' : 'unchanged';
  }

  function bucketDiffFor(lineIndex: number, weekStart: string): BucketDiff | null {
    // An out-of-range week index (metric.values longer than result.weeks, e.g. from an
    // AI-parsed sheet) resolves to '' upstream — mondayOf('') builds an Invalid Date and
    // throws on toISOString(), so short-circuit before it reaches mondayOf.
    if (!weekStart) return null;
    const line = result.value?.productionLines[lineIndex];
    if (!line || !line.terminationTypeId) return null;
    const vendorId = effectiveVendorId(line);
    if (!vendorId) return null;
    return bucketDiffs.value.get(`${vendorId}:${line.terminationTypeId}:${mondayOf(weekStart)}`) ?? null;
  }

  const diffSummary = computed(() => {
    if (!hasExistingData.value || !result.value) return null;
    const summary = { increased: 0, decreased: 0, changed: 0, created: 0, unchanged: 0, skipped: 0, overbooked: 0 };
    bucketDiffs.value.forEach((diff) => {
      if (diff.status === 'new') summary.created += 1;
      else summary[diff.status] += 1;
      if (diff.overbookedBy > 0) summary.overbooked += 1;
    });
    return summary;
  });

  // Weeks already planned for a vendor:termination pair used by this plan, outside the plan's
  // own week set. Display-only awareness — confirm never touches them.
  const uncoveredPlannedWeeks = computed(() => {
    const parsed = result.value;
    if (!parsed) return [];
    const planWeeks = new Set(parsed.weeks.map((week) => mondayOf(week.startDate)));
    const entryKeys = new Set<string>();
    parsed.productionLines.forEach((line) => {
      if (line.skipped || !line.terminationTypeId) return;
      const vendorId = effectiveVendorId(line);
      if (vendorId) entryKeys.add(`${vendorId}:${line.terminationTypeId}`);
    });
    return [...entryKeys].flatMap((entryKey) => {
      const [vendorId, terminationTypeId] = entryKey.split(':');
      return Object.entries(existingAllocations.value[entryKey] ?? {})
        .filter(([weekStart]) => !planWeeks.has(weekStart))
        .map(([weekStart, allocation]) => ({
          vendorId,
          terminationTypeId,
          entryKey,
          weekStart,
          planningCapacity: allocation.planningCapacity ?? 0,
          poCount: allocation.purchaseOrders.length,
        }))
        .sort((a, b) => a.weekStart.localeCompare(b.weekStart));
    });
  });

  const overbookedWeeks = computed<BucketDiff[]>(() =>
    [...bucketDiffs.value.values()].filter((diff) => diff.willWrite && diff.overbookedBy > 0),
  );

  // Overbook consent is for a specific set of buckets — if the set changes, re-ask.
  watch(
    () => [...bucketDiffs.value.values()]
      .filter((diff) => diff.willWrite && diff.overbookedBy > 0)
      .map((diff) => `${diff.vendorId}:${diff.terminationTypeId}:${diff.weekStart}`)
      .sort()
      .join('|'),
    () => {
      overbookAcknowledged.value = false;
    },
  );

  // Saving needs a vendor for every line: a resolved/user-picked line vendor, with the
  // page-level exact match as fallback. The server re-validates both.
  const canSave = computed<boolean>(() => {
    if (!result.value || saveableLines.value.length === 0) return false;
    if (conflictingFactorCodes.value.length > 0) return false;
    const hasExactVendor = result.value.vendorMatch !== null && result.value.vendorMatchConfidence === 'exact';
    const allLinesCarryVendor = saveableLines.value.every((line) => line.vendorId !== null);
    const acknowledged = overbookedWeeks.value.length === 0 || overbookAcknowledged.value;
    return (hasExactVendor || allLinesCarryVendor) && acknowledged;
  });

  // Best-effort: overlap badges degrade to "none shown" if the calendar fetch fails.
  async function loadExistingAllocations(): Promise<void> {
    try {
      existingAllocations.value = (await fetchCapacityCalendar()).allocations;
    } catch {
      existingAllocations.value = {};
    }
  }

  async function parse(file: File): Promise<void> {
    loading.value = true;
    error.value = null;
    result.value = null;
    confirmResult.value = null;
    overwriteWeeks.value = {};
    sessionSavedWeeks.value = new Map();
    try {
      const parsed = await parseCapacityPlan(file);
      result.value = { ...parsed, productionLines: parsed.productionLines.map((line) => ({ ...line, skipped: false })) };
      await loadExistingAllocations();
    } catch (parseError) {
      error.value = extractErrorMessage(parseError);
    } finally {
      loading.value = false;
    }
  }

  async function confirmImport(): Promise<void> {
    const parsed = result.value;
    if (!canSave.value || !parsed) return;
    saving.value = true;
    error.value = null;
    try {
      // Overwrite = user-ticked weeks plus buckets this session already wrote (a re-save
      // after an edit must update them; they need no fresh consent).
      const overwriteByKey = new Map(sessionSavedWeeks.value);
      const writtenNow = new Map<string, OverwriteTarget>();
      parsed.productionLines.forEach((line, lineIndex) => {
        if (line.skipped) return;
        const vendorId = effectiveVendorId(line);
        const { terminationTypeId } = line;
        if (!vendorId || !terminationTypeId) return;
        const capacityValues = line.metrics.find((metric) => metric.key === 'capacity')?.values ?? [];
        parsed.weeks.forEach((week, weekIndex) => {
          const weekStart = mondayOf(week.startDate);
          const bucketKey = `${vendorId}:${terminationTypeId}:${weekStart}`;
          const ticked = overwriteWeeks.value[`${lineIndex}:${weekStart}`] === true;
          if (ticked) overwriteByKey.set(bucketKey, { vendorId, terminationTypeId, weekStart });
          const bucketExists = existingAllocations.value[`${vendorId}:${terminationTypeId}`]?.[weekStart] !== undefined;
          const willWrite = (capacityValues[weekIndex] ?? null) !== null && (!bucketExists || ticked || sessionSavedWeeks.value.has(bucketKey));
          if (willWrite) writtenNow.set(bucketKey, { vendorId, terminationTypeId, weekStart });
        });
      });

      confirmResult.value = await confirmCapacityImport({
        vendorId: parsed.vendorMatchConfidence === 'exact' ? (parsed.vendorMatch?.id ?? null) : null,
        importRecordId: parsed.importRecordId,
        overwrite: [...overwriteByKey.values()],
        weeks: parsed.weeks,
        productionLines: parsed.productionLines.filter((line) => !line.skipped),
      });
      writtenNow.forEach((target, bucketKey) => sessionSavedWeeks.value.set(bucketKey, target));
      await loadExistingAllocations();
    } catch (saveError) {
      error.value = extractErrorMessage(saveError);
    } finally {
      saving.value = false;
    }
  }

  async function loadRecords(): Promise<void> {
    recordsLoading.value = true;
    try {
      records.value = await fetchImportRecords();
    } catch {
      records.value = [];
    } finally {
      recordsLoading.value = false;
    }
  }

  async function deleteRecord(id: string): Promise<boolean> {
    try {
      await deleteImportRecord(id);
      records.value = records.value.filter((record) => record.id !== id);
      return true;
    } catch (deleteError) {
      logger.showError(extractErrorMessage(deleteError, 'Failed to delete the import record'));
      return false;
    }
  }

  async function loadRecord(id: string): Promise<void> {
    loading.value = true;
    error.value = null;
    confirmResult.value = null;
    try {
      const detail = await fetchImportRecord(id);
      if (!detail.parsedPreview) {
        error.value = `Import "${detail.filename}" has no stored parse result`;
        return;
      }
      overwriteWeeks.value = {};
      sessionSavedWeeks.value = new Map();
      await loadExistingAllocations();
      // Best-effort: if the vendor list is unavailable, load the snapshot ids as stored.
      // Superadmins resolve against the full vendor directory (GET /vendors); partners can't
      // call that endpoint (403), so they rebind against the vendors already scoped to their
      // account on /me instead of hitting the network.
      const accessStore = useAccessStore();
      const vendors: VendorSummary[] | null =
        accessStore.me && Array.isArray(accessStore.me.vendors) ? accessStore.me.vendors : await fetchVendors().catch(() => null);
      const preview = vendors ? rebindVendors(detail.parsedPreview, vendors) : detail.parsedPreview;
      // Older previews may predate per-line vendor fields and metric keys; normalize them.
      result.value = {
        ...preview,
        importRecordId: detail.id,
        productionLines: preview.productionLines.map((line) => ({
          ...line,
          vendorCode: line.vendorCode ?? null,
          vendorId: line.vendorId ?? null,
          skipped: false,
          metrics: line.metrics.map((metric) => ({ ...metric, key: metric.key ?? inferMetricKey(metric.name) })),
        })),
      };
    } catch (loadError) {
      error.value = extractErrorMessage(loadError);
    } finally {
      loading.value = false;
    }
  }

  // Manual corrections to the parsed preview. Edits that change what would be saved
  // clear the previous confirmation so the save button reappears.
  function updateMetricValue(lineIndex: number, metricIndex: number, weekIndex: number, value: number | null): void {
    const metric = result.value?.productionLines[lineIndex]?.metrics[metricIndex];
    if (!metric || weekIndex >= metric.values.length) return;
    metric.values[weekIndex] = value;
    confirmResult.value = null;
  }

  // Fixes the parser guessing the wrong year for the whole sheet in one edit — every week's
  // year is replaced (month/day untouched), then re-snapped to Monday/recomputed like the
  // backend would. existingWeekCounts/weekAlreadyPlanned/plannedPoTotal read straight off
  // result.value.weeks, so overlap re-validates immediately with no extra fetch —
  // existingAllocations already holds the whole calendar, not just this plan's original range.
  function updateWeeksYear(newYear: number): void {
    const parsed = result.value;
    if (!parsed || parsed.weeks.length === 0 || !Number.isInteger(newYear)) return;
    parsed.weeks = parsed.weeks.map((week) => snappedWeek(withYear(week.startDate, newYear)));
    confirmResult.value = null;
  }

  // Sets one week's full date range from a date-range picker. Start snaps to Monday (matching
  // what the backend will actually save under); end is stored as picked. Refuses a start that
  // collides with another week's — the backend sums same-key weeks together per termination
  // type, so a silent collision here would double-count capacity rather than error.
  function updateWeekRange(weekIndex: number, newStartDate: string, newEndDate: string): void {
    const parsed = result.value;
    const week = parsed?.weeks[weekIndex];
    if (!parsed || !week || !newStartDate || !newEndDate) return;
    const weekStart = mondayOf(newStartDate);
    const collidesWith = parsed.weeks.find((candidate, index) => index !== weekIndex && mondayOf(candidate.startDate) === weekStart);
    if (collidesWith) {
      logger.showError(`That date falls in the same week as the ${collidesWith.startDate} column (starting ${weekStart}) — combine them instead of using the same week twice`);
      return;
    }
    if (newEndDate < weekStart) {
      logger.showError(`Week end (${newEndDate}) can't be before its start (${weekStart})`);
      return;
    }
    parsed.weeks[weekIndex] = { weekNumber: isoWeekNumber(weekStart), startDate: weekStart, endDate: newEndDate };
    confirmResult.value = null;
  }

  function updateCapacityFactor(lineIndex: number, factor: number | null): void {
    const line = result.value?.productionLines[lineIndex];
    if (!line) return;
    line.capacityFactor = factor;
    confirmResult.value = null;
  }

  // A skipped line is excluded from the confirm payload entirely — it never reaches the
  // backend, so its capacity factor/termination type can't block or corrupt anything else.
  function toggleLineSkipped(lineIndex: number): void {
    const line = result.value?.productionLines[lineIndex];
    if (!line) return;
    line.skipped = !line.skipped;
    confirmResult.value = null;
  }

  // An overwrite tick is consent for a specific vendor:terminationType bucket — retargeting
  // the line would silently transfer that consent, so drop the line's ticks instead.
  function clearLineOverwrites(lineIndex: number): void {
    Object.keys(overwriteWeeks.value)
      .filter((key) => key.startsWith(`${lineIndex}:`))
      .forEach((key) => delete overwriteWeeks.value[key]);
  }

  function updateTerminationType(lineIndex: number, type: CapacityType): void {
    const line = result.value?.productionLines[lineIndex];
    if (!line) return;
    line.terminationTypeCode = type.code;
    line.terminationTypeId = type.id;
    clearLineOverwrites(lineIndex);
    confirmResult.value = null;
  }

  function updateLineVendor(lineIndex: number, vendorId: string): void {
    const line = result.value?.productionLines[lineIndex];
    if (!line) return;
    line.vendorId = vendorId;
    clearLineOverwrites(lineIndex);
    confirmResult.value = null;
  }

  function reset(): void {
    result.value = null;
    error.value = null;
    confirmResult.value = null;
    overwriteWeeks.value = {};
    sessionSavedWeeks.value = new Map();
  }

  return {
    result,
    loading,
    saving,
    error,
    confirmResult,
    records,
    recordsLoading,
    overwriteWeeks,
    existingAllocations,
    existingWeekCounts,
    totalExistingOverlap,
    plannedPoTotal,
    weekAlreadyPlanned,
    plannedPoCount,
    isOverwriteSelected,
    toggleOverwriteWeek,
    extractedSettings,
    conflictingFactorCodes,
    saveableLines,
    canSave,
    hasExistingData,
    bucketDiffs,
    bucketDiffFor,
    diffSummary,
    uncoveredPlannedWeeks,
    overbookedWeeks,
    overbookAcknowledged,
    parse,
    confirmImport,
    loadRecords,
    loadRecord,
    deleteRecord,
    loadExistingAllocations,
    updateMetricValue,
    updateCapacityFactor,
    updateWeeksYear,
    updateWeekRange,
    toggleLineSkipped,
    updateTerminationType,
    updateLineVendor,
    reset,
  };
});
